import { PortfolioLpoa } from '../clients/graphql/pages/lpoa-queries';

export interface LpoaStatus {
  isActive: boolean;
  isSigned: boolean;
  signedAt: Date | null;
  activatedAt: Date | null;
  rejectedAt: Date | null;
  revokedAt: Date | null;
}

/**
 * Determines the LPOA status for a given LPOA record
 * A user is only connected under LPOA if:
 * - activatedAt timestamp exists
 * - revokedAt is null
 * - rejectedAt is null
 */
export function getLpoaStatus(lpoaRecord: PortfolioLpoa): LpoaStatus {
  const isActive = !!(
    lpoaRecord.activatedAt && 
    !lpoaRecord.revokedAt && 
    !lpoaRecord.rejectedAt
  );
  
  const isSigned = !!lpoaRecord.signedAt;
  
  return {
    isActive,
    isSigned,
    signedAt: lpoaRecord.signedAt,
    activatedAt: lpoaRecord.activatedAt,
    rejectedAt: lpoaRecord.rejectedAt,
    revokedAt: lpoaRecord.revokedAt,
  };
}

/**
 * Checks if LPOA document has been created (has envelopeId but no signedAt)
 */
export function isDocumentCreated(lpoaRecord: PortfolioLpoa): boolean {
  return !!(lpoaRecord.envelopeId && !lpoaRecord.signedAt);
}

/**
 * Checks if LPOA has been rejected by Schwab
 */
export function isRejected(lpoaRecord: PortfolioLpoa): boolean {
  return !!lpoaRecord.rejectedAt;
}

/**
 * Checks if LPOA has been revoked by user
 */
export function isRevoked(lpoaRecord: PortfolioLpoa): boolean {
  return !!lpoaRecord.revokedAt;
}

/**
 * Gets the overall LPOA status for a user based on all their LPOA records
 * Returns the status of the most recent LPOA record
 */
export function getUserLpoaStatus(lpoaData: PortfolioLpoa[] | undefined): LpoaStatus | null {
  if (!lpoaData || lpoaData.length === 0) {
    return null;
  }
  
  // Sort by creation date (most recent first) and get the first one
  const sortedLpoa = [...lpoaData].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  
  return getLpoaStatus(sortedLpoa[0]);
}

/**
 * Checks if user has any active LPOA records
 */
export function hasActiveLpoa(lpoaData: PortfolioLpoa[] | undefined): boolean {
  if (!lpoaData || lpoaData.length === 0) {
    return false;
  }
  
  return lpoaData.some(record => {
    const status = getLpoaStatus(record);
    return status.isActive;
  });
}

/**
 * Checks if user has any signed LPOA records (but not necessarily active)
 */
export function hasSignedLpoa(lpoaData: PortfolioLpoa[] | undefined): boolean {
  if (!lpoaData || lpoaData.length === 0) {
    return false;
  }
  
  return lpoaData.some(record => {
    const status = getLpoaStatus(record);
    return status.isSigned;
  });
}